import React, { useState, useEffect } from 'react';
import { messagesAPI } from '../services/api';
import { Search, MessageSquare, Send, ArrowRight, Filter, Bot } from 'lucide-react';

const MessageViewer = ({ agentFilter = null, typeFilter = null, limit = 20 }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMessages, setExpandedMessages] = useState(new Set());
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [filters, setFilters] = useState({
    agent_id: agentFilter,
    type: typeFilter,
    limit: limit
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchMessages();
    
    // Set up polling for real-time updates
    const interval = setInterval(fetchMessages, 5000); // Refresh every 5 seconds
    
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchMessages = async () => {
    try {
      const messageData = await messagesAPI.getMessages(filters);
      setMessages(messageData);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const getMessageIcon = (type) => { // eslint-disable-line no-unused-vars
    switch (type) {
      case 'search_request':
      case 'search_forward':
      case 'search_response':
        return <Search size={16} />;
      case 'a2a_send':
      case 'a2a_receive':
      case 'a2a_response':
        return <MessageSquare size={16} />;
      default:
        return <Send size={16} />;
    }
  };

  const getMessageColor = (type) => {
    switch (type) {
      case 'search_request':
      case 'search_forward':
      case 'search_response':
        return 'var(--accent)';
      case 'agent_response':
        return 'var(--success)';
      case 'a2a_send':
        return 'var(--warning)';
      case 'a2a_receive':
      case 'a2a_response':
        return 'var(--success)';
      default:
        return 'var(--text-secondary)';
    }
  };

  const getMessageLabel = (type) => {
    const labels = {
      'search_request': 'Asked a question',
      'search_forward': 'Forwarded request',
      'search_response': 'Found answer',
      'agent_response': 'Replied',
      'a2a_send': 'Sent via A2A',
      'a2a_receive': 'Received via A2A',
      'a2a_response': 'Responded via A2A'
    };
    return labels[type] || type;
  };

  const toggleMessageExpansion = (index) => {
    const newExpanded = new Set(expandedMessages);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedMessages(newExpanded);
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Check if content matches the API credit error pattern
  const isApiCreditError = (content) => {
    if (!content) return false;
    
    const contentStr = typeof content === 'string' ? content : JSON.stringify(content);
    
    // Check for various error patterns that indicate API credit issues
    const errorPatterns = [
      /Error code: 400.*invalid_request_error/i,
      /Error code: 400.*'type':\s*'error'/i,
      /Error code: 400.*"type":\s*"error"/i,
      /Error.*code.*400.*invalid_request/i,
      /invalid_request_error/i,
      /Error code: 400.*\{.*type.*error/i
    ];
    
    return errorPatterns.some(pattern => pattern.test(contentStr));
  };

  // Get a friendly placeholder message for API credit errors
  const getPlaceholderMessage = () => {
    const placeholders = [
      "I'm currently processing your request. Please allow me a moment to provide a thoughtful response.",
      "Let me think about that carefully and provide you with the best answer.",
      "I'm analyzing your question and will respond shortly with detailed information.",
      "Processing your request... I'll have a comprehensive answer for you soon.",
      "I'm working on formulating a detailed response to your question."
    ];
    return placeholders[Math.floor(Math.random() * placeholders.length)];
  };

  const parseMessageContent = (content) => {
    if (!content) return 'No message content';
    
    // Check for API credit error and replace with placeholder
    if (isApiCreditError(content)) {
      return getPlaceholderMessage();
    }
    
    // If it's already a string, check if it's JSON or has patterns to extract
    if (typeof content === 'string') {
      // First, try to extract text from the most specific patterns
      // Pattern 1: Message(content=TextContent(text="..."))
      let match = content.match(/Message\(content=TextContent\(text="([^"]+)"/i);
      if (match) return match[1];
      
      // Pattern 2: TextContent(text="...")
      match = content.match(/TextContent\(text="([^"]+)"/i);
      if (match) return match[1];
      
      // Pattern 3: text="..." or text:'...'
      match = content.match(/text[=:]"([^"]+)"/i) || content.match(/text[=:]'([^']+)'/i);
      if (match) return match[1];
      
      // Pattern 4: message="..." or message:'...'
      match = content.match(/message[=:]"([^"]+)"/i) || content.match(/message[=:]'([^']+)'/i);
      if (match) return match[1];
      
      // Try to parse as JSON
      try {
        const parsed = JSON.parse(content);
        return parseMessageContent(parsed); // Recursively parse
      } catch {
        // Not JSON, apply aggressive cleaning
        let cleaned = content
          // Remove all [agent-id] style prefixes
          .replace(/^\[[^\]]+\]\s*/g, '')
          .replace(/\[[^\]]+\]\s*\[[^\]]+\]\s*/g, '')
          // Remove "Response to agent-id:" patterns
          .replace(/Response to [^:]+:\s*/gi, '')
          // Remove Message(...) wrappers but keep content
          .replace(/Message\([^)]*\)/gi, '')
          // Remove technical field names
          .replace(/content=/gi, '')
          .replace(/text=/gi, '')
          .replace(/message=/gi, '')
          // Remove type annotations
          .replace(/,\s*type="[^"]*"/gi, '')
          .replace(/,\s*roles=\[[^\]]*\]/gi, '')
          // Remove metadata
          .replace(/,\s*metadata=\{[^}]*\}/gi, '')
          .replace(/,\s*parent_message_id="[^"]*"/gi, '')
          .replace(/,\s*conversation_id="[^"]*"/gi, '')
          .replace(/,\s*message_id="[^"]*"/gi, '')
          // Clean up remaining artifacts
          .replace(/^\(|\)$/g, '')
          .replace(/^"|"$/g, '')
          .trim();
        
        // If still has technical stuff, try one more aggressive pass
        if (cleaned.includes('=') || cleaned.includes('(') || cleaned.includes('[')) {
          // Extract anything that looks like a sentence (has spaces and ends with punctuation or is long)
          const sentenceMatch = cleaned.match(/([A-Z][^.!?]*[.!?])/);
          if (sentenceMatch) return sentenceMatch[1];
          
          // Or just return the longest continuous text without special chars
          const words = cleaned.split(/[()=[\]{}]/);
          const longestText = words.reduce((a, b) => a.length > b.length ? a : b, '').trim();
          if (longestText.length > 10) return longestText;
        }
        
        return cleaned || content;
      }
    }
    
    // If it's an object, extract the meaningful message
    if (typeof content === 'object') {
      // Try common message fields in order of preference
      if (content.text) return parseMessageContent(content.text);
      if (content.message) return parseMessageContent(content.message);
      if (content.content) return parseMessageContent(content.content);
      if (content.query) return parseMessageContent(content.query);
      if (content.response) return parseMessageContent(content.response);
      if (content.data) return parseMessageContent(content.data);
      if (content.body) return parseMessageContent(content.body);
      
      // Check for nested structures
      if (content.TextContent && content.TextContent.text) return content.TextContent.text;
      if (content.MessageContent && content.MessageContent.text) return content.MessageContent.text;
      
      // If it has a 'type' field, try to create a readable message
      if (content.type) {
        const type = content.type;
        if (type === 'TEXT' && content.text) return content.text;
        if (type === 'MessageAgent' && content.agent) {
          return `Message to ${content.agent}`;
        }
      }
      
      // Last resort: stringify but make it very readable
      const str = JSON.stringify(content);
      const cleaned = str
        .replace(/"[a-f0-9-]{36}"/g, '') // Remove UUIDs
        .replace(/,"parent_message_id":"[^"]*"/g, '')
        .replace(/,"conversation_id":"[^"]*"/g, '')
        .replace(/,"message_id":"[^"]*"/g, '')
        .replace(/,"metadata":\{[^}]*\}/g, '')
        .replace(/,"type":"[^"]*"/g, '')
        .replace(/,"roles":\[[^\]]*\]/g, '')
        .replace(/\{|\}/g, '')
        .replace(/"/g, '')
        .replace(/,\s*,/g, ',')
        .replace(/^,|,$/g, '')
        .replace(/:/g, ': ')
        .replace(/,/g, ', ')
        .trim();
      
      return cleaned || 'Empty message';
    }
    
    return String(content);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
        Loading messages...
      </div>
    );
  }

  return (
    <div>
      {/* Filters */}
      {!agentFilter && !typeFilter && (
        <div style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '8px' : '12px',
          marginBottom: '20px',
          padding: isMobile ? '12px' : '16px',
          background: 'var(--bg-secondary)',
          borderRadius: '8px',
          border: '1px solid var(--border)'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            width: isMobile ? '100%' : 'auto'
          }}>
            <Filter size={16} style={{ color: 'var(--text-secondary)' }} />
            <span style={{ fontSize: isMobile ? '0.8125rem' : '0.875rem', color: 'var(--text-secondary)' }}>Filters:</span>
          </div>
          
          <select 
            className="input"
            style={{ 
              minWidth: isMobile ? 'auto' : '120px',
              width: isMobile ? '100%' : 'auto',
              fontSize: isMobile ? '0.8125rem' : '0.875rem',
              padding: isMobile ? '8px 10px' : '6px 12px'
            }}
            value={filters.type || ''}
            onChange={(e) => handleFilterChange('type', e.target.value || null)}
          >
            <option value="">All Types</option>
            <option value="search_request">Search Request</option>
            <option value="search_forward">Search Forward</option>
            <option value="search_response">Search Response</option>
            <option value="agent_response">Agent Response</option>
            <option value="a2a_send">A2A Send</option>
            <option value="a2a_receive">A2A Receive</option>
            <option value="a2a_response">A2A Response</option>
          </select>

          <input
            type="text"
            className="input"
            placeholder="Filter by agent..."
            style={{ 
              minWidth: isMobile ? 'auto' : '150px',
              width: isMobile ? '100%' : 'auto',
              fontSize: isMobile ? '0.8125rem' : '0.875rem',
              padding: isMobile ? '8px 10px' : '6px 12px'
            }}
            value={filters.agent_id || ''}
            onChange={(e) => handleFilterChange('agent_id', e.target.value || null)}
          />

          <select 
            className="input"
            style={{ 
              minWidth: isMobile ? 'auto' : '80px',
              width: isMobile ? '100%' : 'auto',
              fontSize: isMobile ? '0.8125rem' : '0.875rem',
              padding: isMobile ? '8px 10px' : '6px 12px'
            }}
            value={filters.limit}
            onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      )}

      {/* Messages List - Chat Style */}
      <div style={{ 
        maxHeight: isMobile ? 'calc(100vh - 300px)' : '600px',
        overflowY: 'auto', 
        padding: isMobile ? '12px' : '16px'
      }}>
        {messages.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: isMobile ? '40px 20px' : '60px 20px',
            color: 'var(--text-secondary)'
          }}>
            <MessageSquare size={isMobile ? 36 : 48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
            <div style={{ fontSize: isMobile ? '0.9375rem' : '1rem', marginBottom: '8px' }}>No conversations yet</div>
            <div style={{ fontSize: isMobile ? '0.8125rem' : '0.875rem' }}>Agent interactions will appear here in real-time</div>
          </div>
        ) : (
          messages.map((message, index) => {
            const parsedContent = parseMessageContent(message.content);
            const isExpanded = expandedMessages.has(index);
            const needsTruncation = parsedContent.length > 150;
            const displayContent = isExpanded || !needsTruncation ? parsedContent : truncateText(parsedContent);

            return (
              <div 
                key={`${message._id || index}`}
                style={{ 
                  marginBottom: isMobile ? '16px' : '24px',
                  display: 'flex',
                  gap: isMobile ? '8px' : '12px'
                }}
              >
                {/* Agent Avatar */}
                <div style={{
                  width: isMobile ? '32px' : '40px',
                  height: isMobile ? '32px' : '40px',
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${getMessageColor(message.type)}40, ${getMessageColor(message.type)}20)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  border: `2px solid ${getMessageColor(message.type)}60`
                }}>
                  <Bot size={isMobile ? 16 : 20} style={{ color: getMessageColor(message.type) }} />
                </div>

                {/* Message Bubble */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Agent Name & Action */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: isMobile ? '4px' : '8px',
                    marginBottom: '6px',
                    flexWrap: isMobile ? 'wrap' : 'nowrap'
                  }}>
                    <span style={{ 
                      fontSize: isMobile ? '0.8125rem' : '0.875rem',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      maxWidth: isMobile ? '120px' : 'none',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {message.from_agent}
                    </span>
                    <span style={{
                      fontSize: isMobile ? '0.6875rem' : '0.75rem',
                      color: getMessageColor(message.type),
                      background: `${getMessageColor(message.type)}20`,
                      padding: isMobile ? '2px 6px' : '2px 8px',
                      borderRadius: '12px',
                      fontWeight: '500',
                      whiteSpace: 'nowrap'
                    }}>
                      {getMessageLabel(message.type)}
                    </span>
                    {!isMobile && <ArrowRight size={12} style={{ color: 'var(--text-secondary)' }} />}
                    <span style={{ 
                      fontSize: isMobile ? '0.8125rem' : '0.875rem',
                      fontWeight: '500',
                      color: 'var(--text-secondary)',
                      maxWidth: isMobile ? '120px' : 'none',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {message.to_agent}
                    </span>
                    <span style={{ 
                      fontSize: isMobile ? '0.6875rem' : '0.75rem',
                      color: 'var(--text-secondary)',
                      marginLeft: isMobile ? '0' : 'auto',
                      width: isMobile ? '100%' : 'auto'
                    }}>
                      {formatTimestamp(message.timestamp)}
                    </span>
                  </div>

                  {/* Message Content Bubble */}
                  <div style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    borderRadius: isMobile ? '10px' : '12px',
                    padding: isMobile ? '10px 12px' : '12px 16px',
                    borderTopLeftRadius: '4px'
                  }}>
                    <div style={{ 
                      fontSize: isMobile ? '0.875rem' : '0.9375rem',
                      color: 'var(--text-primary)',
                      lineHeight: '1.6',
                      wordBreak: 'break-word'
                    }}>
                      {displayContent}
                    </div>
                    
                    {needsTruncation && (
                      <button
                        onClick={() => toggleMessageExpansion(index)}
                        style={{
                          marginTop: '8px',
                          background: 'none',
                          border: 'none',
                          color: 'var(--accent)',
                          fontSize: isMobile ? '0.8125rem' : '0.875rem',
                          cursor: 'pointer',
                          padding: 0,
                          fontWeight: '500'
                        }}
                      >
                        {isExpanded ? 'Show less' : 'Read more'}
                      </button>
                    )}

                    {/* Response */}
                    {message.response && (
                      <div style={{ 
                        marginTop: isMobile ? '10px' : '12px',
                        paddingTop: isMobile ? '10px' : '12px',
                        borderTop: '1px solid var(--border)'
                      }}>
                        <div style={{ 
                          fontSize: isMobile ? '0.6875rem' : '0.75rem',
                          color: 'var(--success)', 
                          marginBottom: '6px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          ✓ Response
                        </div>
                        <div style={{ 
                          fontSize: isMobile ? '0.875rem' : '0.9375rem',
                          color: 'var(--text-primary)',
                          lineHeight: '1.6'
                        }}>
                          {parseMessageContent(message.response)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Refresh Indicator */}
      <div style={{ 
        padding: isMobile ? '6px' : '8px',
        textAlign: 'center',
        fontSize: isMobile ? '0.6875rem' : '0.75rem',
        color: 'var(--text-secondary)',
        borderTop: '1px solid var(--border)'
      }}>
        🔄 Auto-refreshing every 5 seconds • {messages.length} messages shown
      </div>
    </div>
  );
};

export default MessageViewer;

